import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ProgressBar from './components/ProgressBar';
import AdminProductTaxClass from './admin/product/TaxClass';
import AdminUserRole from './admin/user/UserRole';
import CreateEditRoleForm from './admin/user/CreateEditRoleForm';
import LoginForm from './vendor/LoginForm.';
import RegisterForm from './vendor/RegisterForm';
import VendorMainContent from './vendor/VendorMainContent';
import AdminCustomer from './admin/user/Customer';
import AdminCart from './admin/events/Cart';
import ProtectedRouteVendor from './public/ProtectedRouteVendor';
import VendorDashboard from './vendor/Dashboard';
import InitVendor from './vendor/layouts/Init';
import LoadingDashboardContent from './components/LoadingDashboardContent';
import AdminTags from './admin/Tag';
import AdminPost from './admin/AdminPost';
import SiteInfo from './components/SiteInfo';
import NotFoundPage from './public/404';
import LoginHistory from './admin/LoginHistory';
import { UserProvider } from './hooks/provider/UserProvider';
import { SiteDataProvider } from './hooks/provider/SiteDataProvider';
import AdminWishlist from './admin/events/Wishlist';
import AdminEmails from './admin/AdminEmails';
import AdminSupport from './admin/AdminSupport';
import AdminProfile from './admin/profile/AdminProfile';
import useNetworkStatus from './utils/useNetworkStatus';
import ErrorBanner from './components/ErrorBanner';
import { VendorProvider } from './hooks/provider/VendorProvidor';
import ProtectedRouteAdmin from './public/ProtectedRouteAdmin';
import Reviews from './admin/Reviews';
import VendorResetPasswordForm from './vendor/VendorResetPasswordForm';
import VendorSupport from './vendor/VendorSupport';
import AdminTransactions from './admin/payout/AdminTransactions';
import AdminPayout from './admin/payout/Payout';
import AdminRevenue from './admin/payout/Revenue';
import { useTokens } from './context/TokenContext';
import AdminNotifications from './admin/AdminNotifications';
import VendorCompleteProfile from './vendor/CompleteProfile';
import VendorProfileEdit from './vendor/user/VendorEdit';

const Dashboard = lazy(() => import('./admin/Dashboard'));
const HomePage = lazy(() => import('./public/HomePage'));
const Init = lazy(() => import('./admin/layouts/Init'));
const AdminProduct = lazy(() => import('./admin/product/Product'));
const AdminProductCategory = lazy(() => import('./admin/product/ProductCategory'));
const AdminProductBrand = lazy(() => import('./admin/product/ProductBrand'));
const AdminLogin = lazy(() => import('./admin/Index'));
const AdminPage = lazy(() => import('./admin/AdminPage'));
const AdminUser = lazy(() => import('./admin/user/User'));
const AdminVendor = lazy(() => import('./admin/vendor/Vendor'));
const AdminCoupon = lazy(() => import('./admin/Coupon'));
const AdminTool = lazy(() => import('./admin/Tool'));
const AdminOrder = lazy(() => import('./admin/events/Order'));
const AdminShipments = lazy(() => import('./admin/events/AdminShipments'));
const AdminPickupAddresses = lazy(() => import('./admin/events/AdminPickupAddresses'));
const AdminSetting = lazy(() => import('./admin/setting/Setting'));


// 🔹 App Component
function App() {
  const isOnline = useNetworkStatus();
  const [isLoading, setIsLoading] = useState(false);
  
  const { getItem } = useTokens();
  const AdminToken = getItem('admin', 'AuthToken');  
  const VendorToken = getItem('vendor', 'AuthToken');

  // Handle Route Change (if you have routing in your app)
  const handleRouteChange = () => {
    setIsLoading(true);
  };

  const handleRouteComplete = () => {
    setIsLoading(false);
  };

  if (!isOnline) {
    return <ErrorBanner message={'No internet connection'} />;
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true }}>
      {isLoading && <p>Loading site data...</p>} {/* Display loading message */}      
      <ToastContainer
        position="bottom-right"   
        theme="colored"           
        toastStyle={{ bottom: '40px' }} 
        autoClose={5000}           
        hideProgressBar={false}   
        closeOnClick={true}       
        pauseOnHover={true}       
        draggable={true}          
        pauseOnFocusLoss={true}   
      />
      <ProgressBar isLoading={isLoading} />
      <Suspense fallback={<LoadingDashboardContent />}> {/* Fallback to show a loading indicator */}
        <SiteDataProvider>
          <UserProvider>
            <VendorProvider>
              <SiteInfo />
              <Routes>
                <Route index element={<AdminLogin />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/vendor/complete-profile" element={<VendorCompleteProfile />} />

                <Route path="/vendor" element={<VendorMainContent />}>
                  <Route path="login" element={<LoginForm />} />
                  <Route path="register" element={<RegisterForm />} />
                  <Route path="reset-password" element={<VendorResetPasswordForm />} />
                </Route>

                {/* Vendor Route with Nested Routes */}
                <Route path="/vendor" element={<ProtectedRouteVendor><InitVendor /></ProtectedRouteVendor>}>
                  <Route index element={<VendorDashboard />} />
                  <Route path="dashboard" element={<VendorDashboard />} />

                  <Route path="product" element={<AdminProduct userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="product/new" element={<AdminProduct userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="product/edit/:id" element={<AdminProduct userRole={'vendor'} userToken={VendorToken} />} />

                  <Route path="order/*" element={<AdminOrder userRole={'vendor'} userToken={VendorToken} />} />

                  <Route path="support" element={<VendorSupport />} />
                  <Route path="support/new" element={<VendorSupport />} />
                  <Route path="support/edit/:id" element={<VendorSupport />} /> 

                  <Route path="notifications" element={<AdminNotifications userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="profile/*" element={<VendorProfileEdit />} />

                  <Route path="payout" element={<AdminPayout userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="payout/new" element={<AdminPayout userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="payout/edit/:id" element={<AdminPayout userRole={'vendor'} userToken={VendorToken} />} />

                  <Route path="finance" element={<AdminRevenue userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="finance/total-revenue" element={<AdminRevenue userRole={'vendor'} userToken={VendorToken} />} />
                  <Route path="finance/transactions" element={<AdminTransactions userRole={'vendor'} userToken={VendorToken} />} />
                </Route>

                {/* Admin Page Routes */}
                <Route path="/admin" element={<ProtectedRouteAdmin><Init /></ProtectedRouteAdmin>}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />

                  <Route path="page" element={<AdminPage />} />
                  <Route path="page/new" element={<AdminPage />} />
                  <Route path="page/edit/:id" element={<AdminPage />} />

                  <Route path="product" element={<AdminProduct userRole={'admin'} userToken={AdminToken} />} />
                  <Route path="product/new" element={<AdminProduct userRole={'admin'} userToken={AdminToken} />} />
                  <Route path="product/edit/:id" element={<AdminProduct userRole={'admin'} userToken={AdminToken} />} />

                  <Route path="product/category" element={<AdminProductCategory />} />
                  <Route path="product/category/new" element={<AdminProductCategory />} />
                  <Route path="product/category/edit/:id" element={<AdminProductCategory />} />

                  <Route path="product/brand" element={<AdminProductBrand />} />
                  <Route path="product/brand/new" element={<AdminProductBrand />} />
                  <Route path="product/brand/edit/:id" element={<AdminProductBrand />} />

                  <Route path="product/taxclass" element={<AdminProductTaxClass />} />
                  <Route path="product/taxclass/new" element={<AdminProductTaxClass />} />
                  <Route path="product/taxclass/edit/:id" element={<AdminProductTaxClass />} />

                  <Route path="post" element={<AdminPost />} />
                  <Route path="post/new" element={<AdminPost />} />
                  <Route path="post/edit/:id" element={<AdminPost />} />

                  <Route path="tag" element={<AdminTags />} />
                  <Route path="tag/new" element={<AdminTags />} />
                  <Route path="tag/edit/:id" element={<AdminTags />} />

                  <Route path="review" element={<Reviews />} />
                  <Route path="review/new" element={<Reviews />} />
                  <Route path="review/edit/:id" element={<Reviews />} />

                  <Route path="user" element={<AdminUser />} />
                  <Route path="user/new" element={<AdminUser />} />
                  <Route path="user/edit/:id" element={<AdminUser />} />

                  <Route path="user-role" element={<AdminUserRole />} />
                  <Route path="user-role/new" element={<CreateEditRoleForm />} />
                  <Route path="user-role/edit/:id" element={<CreateEditRoleForm />} />

                  <Route path="vendor" element={<AdminVendor />} />
                  <Route path="vendor/new" element={<AdminVendor />} />
                  <Route path="vendor/edit/:id/*" element={<AdminVendor />} />

                  <Route path="customer" element={<AdminCustomer />} />
                  <Route path="customer/new" element={<AdminCustomer />} />
                  <Route path="customer/edit/:id/*" element={<AdminCustomer />} />

                  <Route path="coupon" element={<AdminCoupon />} />
                  <Route path="coupon/new" element={<AdminCoupon />} />
                  <Route path="coupon/edit/:id" element={<AdminCoupon />} />

                  <Route path="order/*" element={<AdminOrder userRole={'admin'} userToken={AdminToken} />} />

                  <Route path="shipments/*" element={<AdminShipments userRole={'admin'} userToken={AdminToken} />} />

                  <Route path="pickup-addresses/*" element={<AdminPickupAddresses userRole={'admin'} userToken={AdminToken} />} />

                  <Route path="support" element={<AdminSupport />} />
                  <Route path="support/new" element={<AdminSupport />} />
                  <Route path="support/edit/:id" element={<AdminSupport />} />

                  <Route path="wishlist" element={<AdminWishlist />} />
                  <Route path="carts" element={<AdminCart />} />
                  <Route path="email-history" element={<AdminEmails />} />
                  <Route path="login-history" element={<LoginHistory />} />

                  <Route path="tool" element={<AdminTool />} />
                  <Route path="setting/*" element={<AdminSetting />} />
                  <Route path="profile/*" element={<AdminProfile />} />

                  <Route path="notifications" element={<AdminNotifications userRole={'admin'} userToken={AdminToken} />} />
                  <Route path="invoices" element={<AdminTool />} />
                  <Route path="payout" element={<AdminPayout userRole={'admin'} userToken={AdminToken}  />} />
                  <Route path="payout/new" element={<AdminPayout userRole={'admin'} userToken={AdminToken}  />} />
                  <Route path="payout/edit/:id" element={<AdminPayout userRole={'admin'} userToken={AdminToken}  />} />

                  <Route path="finance" element={<AdminRevenue userRole={'admin'} userToken={AdminToken}  />} />
                  <Route path="finance/total-revenue" element={<AdminRevenue userRole={'admin'} userToken={AdminToken}  />} />
                  <Route path="finance/transactions" element={<AdminTransactions userRole={'admin'} userToken={AdminToken}  />} />

                </Route>

                <Route path="*" element={<NotFoundPage />} />

              </Routes>
            </VendorProvider>
          </UserProvider>
        </SiteDataProvider>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
